if (!customElements.get('build-your-bundle')) {
  customElements.define(
    'build-your-bundle',
    class BuildYourBundle extends HTMLElement {
      constructor() {
        super();

        this.form = null;
        this.productInfo = null;
        this.inputContainer = null;
        this.mainVariantInput = null;
        this.submitButton = null;
        this.submitText = null;
        this.submitPriceWrap = null;
        this.submitComparePrice = null;
        this.submitTotalPrice = null;
        this.countEl = this.querySelector('[data-build-bundle-count]');
        this.productsWrap = this.querySelector('[data-build-bundle-products-wrap]');
        this.productsEl = this.querySelector('[data-build-bundle-products]');
        this.scrollHint = this.querySelector('[data-build-bundle-scroll-hint]');
        this.subscriptionFallback = this.querySelector('[data-build-bundle-subscription-fallback]');
        this.items = Array.from(this.querySelectorAll('[data-build-bundle-item]'));
        this.selected = new Map();
        this.maxItems = Number(this.dataset.maxItems) || 10;
        this.priceData = null;
        this.defaultSubmitText = window.variantStrings?.addToCart || 'Add to cart';
        this.boundHandleClick = this.handleClick.bind(this);
        this.boundHandleWheel = this.handleWheel.bind(this);
        this.boundHandleSubmit = this.handleSubmit.bind(this);
        this.boundHandleFormChange = this.handleFormChange.bind(this);
        this.boundHandleProductInfoUpdate = this.handleProductInfoUpdate.bind(this);
        this.boundUpdateScrollHint = this.updateScrollHint.bind(this);
        this.boundHandleSellingPlanChange = this.handleSellingPlanChange.bind(this);
        this.boundHandleSellingPlanClick = this.handleSellingPlanClick.bind(this);
        this.resizeObserver = null;
        this.appWidgetObserver = null;
        this.variantChangeUnsubscriber = null;
        this.bundleScrollTarget = 0;
        this.bundleScrollFrame = null;
        this.updateCountTimer = null;
        this.itemCache = new Map();
      }

      connectedCallback() {
        this.form = this.getForm();
        this.productInfo = this.getProductInfo();
        this.inputContainer = this.form?.querySelector('[data-build-bundle-inputs]');
        this.mainVariantInput = this.form?.querySelector('[data-build-bundle-main-input]');
        this.submitButton = this.form?.querySelector('[data-build-bundle-submit]');
        this.submitText = this.submitButton?.querySelector('[data-build-bundle-submit-text]') || this.submitButton?.querySelector('span');
        this.submitPriceWrap = this.submitButton?.querySelector('[data-build-bundle-submit-prices]');
        this.submitComparePrice = this.submitButton?.querySelector('[data-build-bundle-submit-compare]');
        this.submitTotalPrice = this.submitButton?.querySelector('[data-build-bundle-submit-total]');
        this.defaultSubmitText = this.submitText?.textContent?.trim() || this.defaultSubmitText;

        if (!this.form || !this.inputContainer || !this.submitButton) return;

        this.populateItemCache();
        this.addEventListener('click', this.boundHandleClick);
        this.addEventListener('change', this.boundHandleSellingPlanChange);
        document.addEventListener('change', this.boundHandleSellingPlanChange);
        document.addEventListener('click', this.boundHandleSellingPlanClick);
        document.addEventListener('product-info:loaded', this.boundHandleProductInfoUpdate);
        this.form.addEventListener('submit', this.boundHandleSubmit, true);
        this.form.addEventListener('change', this.boundHandleFormChange);
        this.form.addEventListener('input', this.boundHandleFormChange);
        if (this.productInfo && this.productInfo !== this.form) {
          this.productInfo.addEventListener('change', this.boundHandleFormChange);
          this.productInfo.addEventListener('input', this.boundHandleFormChange);
        }
        this.productsEl?.addEventListener('wheel', this.boundHandleWheel, { passive: false });
        this.productsEl?.addEventListener('scroll', this.boundUpdateScrollHint, { passive: true });
        window.addEventListener('resize', this.boundUpdateScrollHint);

        if (window.PUB_SUB_EVENTS?.variantChange && typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
            window.setTimeout(this.boundHandleProductInfoUpdate, 0);
          });
        }

        if (window.ResizeObserver && this.productsEl) {
          this.resizeObserver = new ResizeObserver(this.boundUpdateScrollHint);
          this.resizeObserver.observe(this.productsEl);
        }

        this.appWidgetObserver = new MutationObserver(() => this.updateSubscriptionFallback());
        this.appWidgetObserver.observe(document.body, { childList: true, subtree: true });
        this.updateSubscriptionFallback();
        this.update();
        window.requestAnimationFrame(this.boundUpdateScrollHint);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.boundHandleClick);
        this.removeEventListener('change', this.boundHandleSellingPlanChange);
        document.removeEventListener('change', this.boundHandleSellingPlanChange);
        document.removeEventListener('click', this.boundHandleSellingPlanClick);
        document.removeEventListener('product-info:loaded', this.boundHandleProductInfoUpdate);
        this.form?.removeEventListener('submit', this.boundHandleSubmit, true);
        this.form?.removeEventListener('change', this.boundHandleFormChange);
        this.form?.removeEventListener('input', this.boundHandleFormChange);
        if (this.productInfo && this.productInfo !== this.form) {
          this.productInfo.removeEventListener('change', this.boundHandleFormChange);
          this.productInfo.removeEventListener('input', this.boundHandleFormChange);
        }
        this.productsEl?.removeEventListener('wheel', this.boundHandleWheel);
        this.productsEl?.removeEventListener('scroll', this.boundUpdateScrollHint);
        window.removeEventListener('resize', this.boundUpdateScrollHint);
        this.resizeObserver?.disconnect();
        this.appWidgetObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        if (this.bundleScrollFrame) window.cancelAnimationFrame(this.bundleScrollFrame);
        if (this.updateCountTimer) clearTimeout(this.updateCountTimer);
      }

      get tier() {
        if (this.total >= 4) return '4';
        if (this.total >= 1) return String(this.total);
        return '';
      }

      get total() {
        return Array.from(this.selected.values()).reduce((sum, quantity) => sum + quantity, 0);
      }

      get isFull() {
        return this.total >= this.maxItems;
      }

      getForm() {
        if (this.dataset.productFormId) return document.getElementById(this.dataset.productFormId);
        return this.closest('form');
      }

      handleClick(event) {
        const item = event.target.closest('[data-build-bundle-item]');
        if (!item || item.dataset.available !== 'true') return;

        if (event.target.closest('[data-build-bundle-minus]')) {
          this.changeQuantity(item, -1);
          return;
        }

        if (event.target.closest('[data-build-bundle-add], [data-build-bundle-plus]')) {
          this.changeQuantity(item, 1);
        }
      }

      handleSubmit(event) {
        if (this.total > this.maxItems) {
          event.preventDefault();
          this.trimToMaxItems();
        }

        this.update();
      }

      handleFormChange(event) {
        if (!event.target?.matches?.('[name="quantity"], [name="id"], [name^="options["]')) return;

        window.setTimeout(() => this.update(), 0);
      }

      handleProductInfoUpdate() {
        window.setTimeout(() => this.update(), 0);
      }

      handleSellingPlanChange(event) {
        if (!this.isSellingPlanEventTarget(event.target)) return;

        this.update();
      }

      handleSellingPlanClick(event) {
        if (!this.isSellingPlanEventTarget(event.target)) return;

        window.setTimeout(() => this.update(), 150);
      }

      isSellingPlanEventTarget(target) {
        return Boolean(
          target?.matches?.(
            '[name="selling_plan"], [data-build-bundle-selling-plan], [data-build-bundle-selling-plan-choice]'
          ) ||
          target?.closest?.('recharge-subscription-widget, .appstle_sub_widget, [id^="appstle_subscription_widget"]')
        );
      }

      handleWheel(event) {
        if (!this.productsEl || window.matchMedia('(max-width: 749px)').matches) return;

        const scrollableDistance = this.productsEl.scrollHeight - this.productsEl.clientHeight;
        if (scrollableDistance <= 4) return;

        const rawDelta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
        const delta = rawDelta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.productsEl.clientHeight : 1);
        const isScrollingDown = delta > 0;
        const isScrollingUp = delta < 0;
        const canScrollDown = this.productsEl.scrollTop < scrollableDistance - 1;
        const canScrollUp = this.productsEl.scrollTop > 1;

        if ((isScrollingDown && canScrollDown) || (isScrollingUp && canScrollUp)) {
          event.preventDefault();
          event.stopPropagation();
          event.lenisStopPropagation = true;
          this.bundleScrollTarget = this.clamp(
            (this.bundleScrollFrame ? this.bundleScrollTarget : this.productsEl.scrollTop) + delta,
            0,
            scrollableDistance
          );
          this.animateBundleScroll();
        }
      }

      animateBundleScroll() {
        if (!this.productsEl || this.bundleScrollFrame) return;

        const scroll = () => {
          if (!this.productsEl) return;

          const distance = this.bundleScrollTarget - this.productsEl.scrollTop;
          if (Math.abs(distance) < 0.6) {
            this.productsEl.scrollTop = this.bundleScrollTarget;
            this.bundleScrollFrame = null;
            this.updateScrollHint();
            return;
          }

          this.productsEl.scrollTop += distance * 0.18;
          this.updateScrollHint();
          this.bundleScrollFrame = window.requestAnimationFrame(scroll);
        };

        this.bundleScrollFrame = window.requestAnimationFrame(scroll);
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }

      populateItemCache() {
        this.itemCache.clear();
        this.items.forEach((item) => {
          this.itemCache.set(item.dataset.variantId, {
            element: item,
            addButton: item.querySelector('[data-build-bundle-add]'),
            addText: item.querySelector('[data-build-bundle-add-text]'),
            quantityWrap: item.querySelector('[data-build-bundle-qty]'),
            quantityText: item.querySelector('[data-build-bundle-quantity]'),
            plusButton: item.querySelector('[data-build-bundle-plus]'),
          });
        });
      }

      changeQuantity(item, direction) {
        if (direction > 0 && this.isFull) return;

        const variantId = item.dataset.variantId;
        const currentQuantity = this.selected.get(variantId) || 0;
        const nextQuantity = currentQuantity + direction;

        if (nextQuantity <= 0) {
          this.selected.delete(variantId);
        } else {
          this.selected.set(variantId, nextQuantity);
        }

        this.update();
      }

      trimToMaxItems() {
        let remaining = this.maxItems;
        const entries = Array.from(this.selected.entries());

        for (const [variantId, quantity] of entries) {
          if (quantity <= remaining) {
            remaining -= quantity;
            continue;
          }
          if (remaining > 0) {
            this.selected.set(variantId, remaining);
          } else {
            this.selected.delete(variantId);
          }
          remaining = 0;
        }
      }

      update() {
        const total = this.total;
        const mainProductAvailable = this.isMainProductAvailable();

        this.renderInputs();
        this.updateItems();

        if (this.countEl) {
          if (this.updateCountTimer) clearTimeout(this.updateCountTimer);
          this.updateCountTimer = setTimeout(() => {
            this.countEl.textContent =
              total === 0 ? 'No add-ons selected' : `${total} add-on${total === 1 ? '' : 's'} selected`;
            this.countEl.classList.toggle('is-maxed', this.isFull);
          }, 150);
        }

        if (this.submitButton && this.submitText) {
          const canSubmit = mainProductAvailable;
          const unavailableText = this.getMainProductVariant()
            ? window.variantStrings?.soldOut || 'Sold out'
            : window.variantStrings?.unavailable || 'Unavailable';
          this.submitButton.disabled = !canSubmit;
          this.submitText.textContent = canSubmit ? this.defaultSubmitText : unavailableText;
          this.updateSubmitPrice(canSubmit);
        }

        this.updateScrollHint();
      }

      updateSubscriptionFallback() {
        if (!this.subscriptionFallback) return;

        const appWidget = Array.from(
          document.querySelectorAll(
            'recharge-subscription-widget, .appstle_sub_widget, [id^="appstle_subscription_widget"]'
          )
        ).find((widget) =>
          Boolean(!widget.closest('[hidden]') && (widget.offsetParent || widget.getClientRects().length))
        );
        this.subscriptionFallback.hidden = Boolean(appWidget);
      }

      getSelectedSellingPlan() {
        const useFallback = this.subscriptionFallback && !this.subscriptionFallback.hidden;
        const fallbackChoice = useFallback
          ? this.querySelector('[data-build-bundle-selling-plan-choice]:checked')
          : null;
        if (fallbackChoice?.value === '') {
          return {
            id: '',
            index: -1,
          };
        }

        const fallbackSelect = useFallback ? this.querySelector('select[data-build-bundle-selling-plan]') : null;
        if (fallbackChoice?.value === 'subscribe' && fallbackSelect?.value) {
          const selectedOption = fallbackSelect.options[fallbackSelect.selectedIndex];
          const fallbackPlans = Array.from(fallbackSelect.options).filter((option) => option.value);

          return {
            id: fallbackSelect.value,
            index: fallbackPlans.indexOf(selectedOption),
          };
        }

        const fallbackPlan = useFallback ? this.querySelector('input[data-build-bundle-selling-plan]:checked') : null;
        if (fallbackPlan) {
          const fallbackPlans = Array.from(this.querySelectorAll('input[data-build-bundle-selling-plan]')).filter(
            (control) => control.value
          );

          return {
            id: fallbackPlan.value,
            index: fallbackPlans.indexOf(fallbackPlan),
          };
        }

        const controls = Array.from(document.querySelectorAll('[name="selling_plan"]'));
        const select = controls.find((control) => control.tagName === 'SELECT' && control.value);
        if (select) {
          const selectedOption = select.options[select.selectedIndex];
          const planOptions = Array.from(select.options).filter((option) => option.value);

          return {
            id: select.value,
            index: planOptions.indexOf(selectedOption),
          };
        }

        const checked = controls.find((control) => control.checked && control.value);
        if (checked) {
          const namedControls = controls.filter(
            (control) => control.name === checked.name && control.value && control.type !== 'hidden'
          );

          return {
            id: checked.value,
            index: namedControls.indexOf(checked),
          };
        }

        const hidden = controls.find((control) => control.type === 'hidden' && control.value);
        if (hidden) {
          return {
            id: hidden.value,
            index: this.getSellingPlanIndexForId(hidden.value),
          };
        }

        const valued = controls.find((control) => control.value);
        return {
          id: valued?.value || '',
          index: valued ? this.getSellingPlanIndexForId(valued.value) : -1,
        };
      }

      getSelectedSellingPlanId() {
        return this.getSelectedSellingPlan().id;
      }

      getProductInfo() {
        return this.closest('product-info');
      }

      getMainProductVariant() {
        const selectedVariant = this.getProductInfo()?.querySelector('variant-selects [data-selected-variant]');
        if (selectedVariant?.textContent) {
          try {
            return JSON.parse(selectedVariant.textContent);
          } catch (error) {
            return null;
          }
        }

        return {
          id: this.dataset.mainVariantId || '',
          available: this.dataset.mainAvailable !== 'false',
          price: Number(this.dataset.mainPrice || 0),
          compare_at_price: Number(this.dataset.mainCompareAtPrice || this.dataset.mainPrice || 0),
        };
      }

      getMainProductVariantId() {
        return String(this.getMainProductVariant()?.id || '');
      }

      isMainProductAvailable() {
        const variant = this.getMainProductVariant();
        if (!variant) return false;
        if (variant && Object.prototype.hasOwnProperty.call(variant, 'available')) return Boolean(variant.available);

        return Boolean(this.getMainProductVariantId()) && this.dataset.mainAvailable !== 'false';
      }

      getQuantityInput() {
        const directQuantityInput = this.form?.querySelector('[name="quantity"]');
        if (directQuantityInput) return directQuantityInput;

        const scope = this.productInfo || this.getProductInfo() || document;
        return Array.from(scope.querySelectorAll('[name="quantity"]')).find((control) => control.form === this.form);
      }

      getMainProductQuantity() {
        const quantity = parseInt(this.getQuantityInput()?.value || '1', 10);
        return quantity > 0 ? quantity : 1;
      }

      getMainPriceData() {
        if (this.priceData) return this.priceData;

        const priceDataScript = this.dataset.sectionId
          ? document.getElementById(`JvMobilePriceData-${this.dataset.sectionId}`)
          : this.getProductInfo()?.querySelector('script[id^="JvMobilePriceData-"]');

        try {
          this.priceData = priceDataScript?.textContent ? JSON.parse(priceDataScript.textContent) : {};
        } catch (error) {
          this.priceData = {};
        }

        return this.priceData;
      }

      getMainProductVariantPriceData() {
        const variantId = this.getMainProductVariantId();
        return this.getMainPriceData()?.variants?.[variantId] || null;
      }

      getMainProductSellingPlanIds() {
        const dataPlanIds = (this.dataset.mainSellingPlanIds || '').split(',').filter(Boolean);
        if (dataPlanIds.length) return dataPlanIds;

        const variantData = this.getMainProductVariantPriceData();
        return variantData?.allocations ? Object.keys(variantData.allocations) : [];
      }

      getMainProductSellingPlanPrices() {
        if (this.mainSellingPlanPrices) return this.mainSellingPlanPrices;

        try {
          this.mainSellingPlanPrices = JSON.parse(`{${this.dataset.mainSellingPlanPrices || ''}}`);
        } catch (error) {
          this.mainSellingPlanPrices = {};
        }

        return this.mainSellingPlanPrices;
      }

      getSellingPlanIdForMainProduct(sellingPlan) {
        const mainSellingPlanIds = this.getMainProductSellingPlanIds();
        if (!sellingPlan?.id || !mainSellingPlanIds.length) return '';

        if (mainSellingPlanIds.includes(sellingPlan.id)) return sellingPlan.id;

        if (sellingPlan.index >= 0 && mainSellingPlanIds[sellingPlan.index]) {
          return mainSellingPlanIds[sellingPlan.index];
        }

        return '';
      }

      getMainProductPrice(sellingPlan) {
        const variant = this.getMainProductVariant();
        const variantData = this.getMainProductVariantPriceData();
        const sellingPlanId = this.getSellingPlanIdForMainProduct(sellingPlan);
        const fallbackPlanPrices = sellingPlanId ? this.getMainProductSellingPlanPrices()[sellingPlanId] : null;
        const planPrices = sellingPlanId ? variantData?.allocations?.[sellingPlanId] || fallbackPlanPrices : null;
        const price = Number(planPrices?.price ?? variantData?.price ?? variant?.price ?? this.dataset.mainPrice ?? 0);
        const compareAtPrice = Number(
          planPrices?.compareAtPrice ??
          variantData?.compareAtPrice ??
          variant?.compare_at_price ??
          this.dataset.mainCompareAtPrice ??
          price
        );

        return {
          price,
          compareAtPrice: compareAtPrice > 0 ? compareAtPrice : price,
        };
      }

      getSellingPlanIndexForId(sellingPlanId) {
        if (!sellingPlanId) return -1;

        const mainSellingPlanIndex = this.getMainProductSellingPlanIds().indexOf(sellingPlanId);
        if (mainSellingPlanIndex !== -1) return mainSellingPlanIndex;

        for (const item of this.items) {
          const itemSellingPlanIds = this.getItemSellingPlanIds(item);
          const planIndex = itemSellingPlanIds.indexOf(sellingPlanId);
          if (planIndex !== -1) return planIndex;
        }

        return -1;
      }

      getItemSellingPlanIds(item) {
        return (item.dataset.sellingPlanIds || '').split(',').filter(Boolean);
      }

      getSellingPlanIdForItem(item, sellingPlan) {
        // Do not apply the subscription/selling plan to the bundle/addon items
        return '';
      }

      getItemSellingPlanPrices(item) {
        if (item.bundleSellingPlanPrices) return item.bundleSellingPlanPrices;

        try {
          item.bundleSellingPlanPrices = JSON.parse(`{${item.dataset.sellingPlanPrices || ''}}`);
        } catch (error) {
          item.bundleSellingPlanPrices = {};
        }

        return item.bundleSellingPlanPrices;
      }

      getItemPrice(item, sellingPlan) {
        const sellingPlanId = this.getSellingPlanIdForItem(item, sellingPlan);
        const planPrices = sellingPlanId ? this.getItemSellingPlanPrices(item)[sellingPlanId] : null;
        const price = Number(planPrices?.price ?? item.dataset.price ?? 0);
        const compareAtPrice = Number(planPrices?.compareAtPrice ?? item.dataset.compareAtPrice ?? price);

        return {
          price,
          compareAtPrice: compareAtPrice > 0 ? compareAtPrice : price,
        };
      }

      getBundlePriceTotals() {
        const sellingPlan = this.getSelectedSellingPlan();
        const mainProductPrice = this.getMainProductPrice(sellingPlan);
        const mainProductQuantity = this.getMainProductQuantity();
        let price = mainProductPrice.price * mainProductQuantity;
        let compareAtPrice = mainProductPrice.compareAtPrice * mainProductQuantity;

        this.items.forEach((item) => {
          const quantity = this.selected.get(item.dataset.variantId) || 0;
          if (!quantity) return;

          const itemPrice = this.getItemPrice(item, sellingPlan);
          price += itemPrice.price * quantity;
          compareAtPrice += itemPrice.compareAtPrice * quantity;
        });

        return { price, compareAtPrice };
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const amount = Number(cents || 0) / 100;
        const values = {
          amount: amount.toFixed(2),
          amount_no_decimals: Math.round(amount).toString(),
          amount_with_comma_separator: amount.toFixed(2).replace('.', ','),
          amount_no_decimals_with_comma_separator: Math.round(amount).toString().replace('.', ','),
        };

        if (!format.includes('{{')) return `$${values.amount}`;

        return format.replace(/\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator)?)\s*\}\}/g, (_, token) => {
          return values[token] || values.amount;
        });
      }

      updateSubmitPrice(canSubmit) {
        if (!this.submitPriceWrap || !this.submitTotalPrice || !this.submitComparePrice) return;

        if (!canSubmit) {
          this.submitPriceWrap.hidden = true;
          this.submitComparePrice.hidden = true;
          this.submitComparePrice.textContent = '';
          this.submitTotalPrice.textContent = '';
          return;
        }

        const totals = this.getBundlePriceTotals();
        this.submitTotalPrice.textContent = this.formatMoney(totals.price);

        const hasComparePrice = totals.compareAtPrice > totals.price;
        this.submitComparePrice.hidden = !hasComparePrice;
        this.submitComparePrice.textContent = hasComparePrice ? this.formatMoney(totals.compareAtPrice) : '';
        this.submitPriceWrap.hidden = false;
      }

      updateScrollHint() {
        if (!this.productsEl || !this.productsWrap || !this.scrollHint) return;

        const scrollableDistance = this.productsEl.scrollHeight - this.productsEl.clientHeight;
        const hasOverflow = scrollableDistance > 4;
        const usesInnerScroll = window.matchMedia('(min-width: 750px)').matches;
        this.productsEl.toggleAttribute('data-lenis-prevent-touch', hasOverflow && usesInnerScroll);

        const lastItem = this.items[this.items.length - 1];
        const lastItemStartsInView = lastItem
          ? lastItem.offsetTop < this.productsEl.scrollTop + this.productsEl.clientHeight - 24
          : true;
        const showHint = hasOverflow && !lastItemStartsInView;

        this.productsWrap.classList.toggle('has-more-products', showHint);
        this.scrollHint.hidden = !showHint;
      }

      updateItems() {
        this.items.forEach((item) => {
          const variantId = item.dataset.variantId;
          const quantity = this.selected.get(variantId) || 0;
          const cache = this.itemCache.get(variantId);
          const { addButton, addText, quantityWrap, quantityText, plusButton } = cache || {};
          const isAvailable = item.dataset.available === 'true';
          const canAddMore = isAvailable && !this.isFull;

          item.classList.toggle('is-selected', quantity > 0);
          if (quantityWrap) quantityWrap.hidden = quantity === 0;
          if (quantityText) quantityText.textContent = quantity;

          if (addButton && isAvailable) {
            addButton.disabled = !canAddMore;
            addButton.hidden = quantity > 0;
          }

          if (addText && isAvailable) addText.textContent = this.isFull ? `Max ${this.maxItems}` : 'Add +';
          if (plusButton) plusButton.disabled = !canAddMore;
        });
      }

      renderInputs() {
        this.inputContainer.innerHTML = '';
        if (this.mainVariantInput) this.mainVariantInput.disabled = true;

        let index = 0;
        const sellingPlan = this.getSelectedSellingPlan();
        const mainVariantId = this.getMainProductVariantId();
        const mainSellingPlanId = this.getSellingPlanIdForMainProduct(sellingPlan);

        if (mainVariantId) {
          this.inputContainer.appendChild(this.createInput(`items[${index}][id]`, mainVariantId));
          this.inputContainer.appendChild(
            this.createInput(`items[${index}][quantity]`, String(this.getMainProductQuantity()))
          );
          if (mainSellingPlanId) {
            this.inputContainer.appendChild(this.createInput(`items[${index}][selling_plan]`, mainSellingPlanId));
          }
          index += 1;
        }

        this.items.forEach((item) => {
          const quantity = this.selected.get(item.dataset.variantId) || 0;
          const itemSellingPlanId = this.getSellingPlanIdForItem(item, sellingPlan);

          for (let count = 0; count < quantity; count += 1) {
            this.inputContainer.appendChild(this.createInput(`items[${index}][id]`, item.dataset.variantId));
            this.inputContainer.appendChild(this.createInput(`items[${index}][quantity]`, '1'));
            if (itemSellingPlanId) {
              this.inputContainer.appendChild(this.createInput(`items[${index}][selling_plan]`, itemSellingPlanId));
            }
            this.inputContainer.appendChild(
              this.createInput(`items[${index}][properties][_Bundle]`, this.dataset.productTitle || 'Build your bundle')
            );
            this.inputContainer.appendChild(
              this.createInput(`items[${index}][properties][_Bundle item]`, item.dataset.productTitle || '')
            );
            this.inputContainer.appendChild(
              this.createInput(
                `items[${index}][properties][_Bundle tier]`,
                this.tier === '4' ? '4+ bags' : `${this.tier} bag${this.tier === '1' ? '' : 's'}`
              )
            );
            if (item.dataset.image) {
              this.inputContainer.appendChild(
                this.createInput(`items[${index}][properties][bundle_image]`, item.dataset.image)
              );
            }
            index += 1;
          }
        });
      }

      createInput(name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        return input;
      }
    }
  );
}
